import React, { useState, useEffect } from 'react';
import { Brain, Terminal, CheckCircle } from 'lucide-react';
import StatCard from '../../../StatCard';
import Decret from '../../../Decret';
import { getIcon, mockSimGameState } from '../helpers';

const BugTerminal = ({ phase }) => {
    const [visibleLines, setVisibleLines] = useState(0);

    const bugCode = [
        { text: '// StatCard.jsx', type: 'comment' },
        { text: '<div className="stat-card">', type: 'code' },
        { text: '  <div className="stat-value">', type: 'code' },
        { text: '    <span>{value}</span>', type: 'code' },
        { text: '    <div onClick={onInvest}', type: 'bug' },
        { text: '         className="bg-yellow-500">', type: 'bug' },
        { text: '      <HandCoins />', type: 'code' },
        { text: '    </div>', type: 'code' },
        { text: '  </div>', type: 'code' },
        { text: '</div>', type: 'code' },
    ];

    const fixCode = [
        { text: '// StatCard.jsx', type: 'comment' },
        { text: '<div className="stat-card"', type: 'code' },
        { text: '     onClick={onInvest}>', type: 'fix' },
        { text: '  <div className="stat-value">', type: 'code' },
        { text: '    <span>{value}</span>', type: 'code' },
        { text: '    <div className="bg-yellow-500">', type: 'code' },
        { text: '      <HandCoins />', type: 'code' },
        { text: '    </div>', type: 'code' },
        { text: '  </div>', type: 'code' },
        { text: '</div>', type: 'code' },
    ];

    const lines = phase === 'fix' ? fixCode : bugCode;

    useEffect(() => {
        setVisibleLines(0);
        let i = 0;
        const interval = setInterval(() => {
            i++;
            setVisibleLines(i);
            if (i >= lines.length) clearInterval(interval);
        }, 350);
        return () => clearInterval(interval);
    }, [phase, lines.length]);

    const getLineClass = (type) => {
        if (type === 'comment') return 'text-slate-500';
        if (type === 'bug') return 'text-red-400 bg-red-500/15';
        if (type === 'fix') return 'text-green-400 bg-green-500/15';
        return 'text-blue-200';
    };

    const renderLine = (line) => line.text;

    return (
        <div className="w-full h-full flex flex-col rounded-xl border border-blue-500/20 overflow-hidden bg-slate-950/90 backdrop-blur-sm animate-fade-in-up text-left">
            {/* Terminal header */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/80 border-b border-blue-500/10">
                <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="flex items-center gap-2 ml-3">
                    <Terminal size={14} className="text-blue-400" />
                    <span className="text-sm font-mono text-blue-300/80">
                        {phase === 'fix' ? 'HOTFIX APPLIED' : 'BUG DETECTED'}
                    </span>
                </div>
                {phase === 'fix' && (
                    <CheckCircle size={14} className="ml-auto text-green-400 animate-pulse" />
                )}
            </div>

            {/* Code content */}
            <div className="flex-1 p-5 font-mono text-base leading-relaxed overflow-hidden">
                {/* Phase label */}
                <div className={`mb-3 px-3 py-1.5 rounded-md text-sm font-bold inline-flex items-center gap-2 ${
                    phase === 'fix'
                        ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                        : 'bg-red-500/15 text-red-400 border border-red-500/30'
                }`}>
                    {phase === 'fix' ? '+ AFTER' : '- BEFORE'}
                </div>

                {/* Code lines */}
                <div className="mt-3">
                    {lines.map((line, i) => (
                        <div
                            key={`${phase}-${i}`}
                            className={`px-3 py-0.5 rounded transition-all duration-700 whitespace-pre ${
                                i < visibleLines ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                            } ${getLineClass(line.type)}`}
                        >
                            <span className="text-slate-600 inline-block w-8 text-right mr-4 select-none">{i + 1}</span>
                            {renderLine(line)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const GameSimulationSingleSlide = ({
    slide,
    slideKey,
    slidesLength,
    currentSlide,
    onSlideChange,
    simInvestments,
    handleSimInvest,
    simLoading,
    simLoadingMessage,
    showSimDecree,
    setShowSimDecree,
    simActions,
    setSimLoading,
    showBugVisualization,
    onBugFix,
    triggerReaction,
    isTalking,
    transitionsEnabled
}) => {
    const [bugFixed, setBugFixed] = useState(false);
    const [terminalPhase, setTerminalPhase] = useState('bug');

    // Reset bugFixed when slideKey changes (slide navigation)
    useEffect(() => {
        setBugFixed(false);
        setTerminalPhase('bug');
    }, [slideKey]);

    const wrappedInvest = (label) => {
        if (showBugVisualization && !bugFixed) {
            setBugFixed(true);
            if (onBugFix) onBugFix();
            setTimeout(() => setTerminalPhase('fix'), 800);
        }
        handleSimInvest(label);
    };

    return (
        <div key={slideKey} className="h-full p-12 flex flex-col justify-center relative">
            <div className="relative z-10 text-center">
                <h2 className="text-6xl font-bold mb-4 pb-2 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up">
                    {slide.title}
                </h2>
                <p className="text-2xl text-blue-200/60 mb-12 animate-fade-in-up stagger-1 max-w-2xl mx-auto">
                    {slide.subtitle}
                </p>

                <div className="flex items-stretch gap-12 max-w-5xl mx-auto">
                    {/* Terminal (always visible) */}
                    <div className="flex-1 min-w-0">
                        <BugTerminal phase={terminalPhase} />
                    </div>

                    {/* StatCard + button */}
                    <div className="flex flex-col items-center gap-12 flex-1">
                        <StatCard
                            label={slide.stat.label}
                            color={slide.stat.color}
                            icon={getIcon(slide.stat.icon)}
                            value={mockSimGameState.joueur[slide.stat.key]}
                            onInvest={wrappedInvest}
                            isSelected={simInvestments.includes(slide.stat.label)}
                            isBugVersion={showBugVisualization}
                        />

                        <button
                            onClick={() => onSlideChange(Math.min(slidesLength - 1, currentSlide + 1))}
                            disabled={simInvestments.length === 0}
                            className={`px-12 py-4 rounded-xl text-xl font-bold transition-all transform hover:scale-105 ${simInvestments.length === 0
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed opacity-50'
                                : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:shadow-[0_0_50px_rgba(16,185,129,0.5)]'
                                }`}
                        >
                            {simInvestments.length === 0 ? "Sélectionnez pour continuer" : "Valider l'Action"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Overlay de chargement simulation */}
            {simLoading && (
                <div className="fixed inset-0 z-[250] bg-slate-900/90 backdrop-blur-xl flex flex-col items-center justify-center animate-fade-in">
                    <div className="relative">
                        <div className="w-32 h-32 rounded-full border-4 border-blue-500/20 border-t-blue-400 animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Brain className="w-12 h-12 text-blue-400 animate-pulse" />
                        </div>
                    </div>
                    <div className="mt-8 text-center">
                        <div className="flex items-center gap-3 justify-center mb-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
                        </div>
                        <p className="text-2xl font-mono text-blue-100/90 animate-pulse">
                            {simLoadingMessage}
                        </p>
                    </div>
                </div>
            )}

            {showSimDecree && (
                <div className="fixed inset-0 z-[200] animate-fade-in">
                    <Decret
                        actions={simActions}
                        gameState={mockSimGameState}
                        setGameState={() => { }}
                        setActions={() => { }}
                        setMessage={() => { }}
                        setLoading={setSimLoading}
                        setPreviousArticles={() => { }}
                        onClose={() => setShowSimDecree(false)}
                        isSimulation={true}
                        triggerReaction={triggerReaction}
                        isTalking={isTalking}
                        transitionsEnabled={transitionsEnabled}
                        onSimulationComplete={() => {
                            setTimeout(() => {
                                setShowSimDecree(false);
                                onSlideChange(currentSlide + 1);
                            }, 100);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default GameSimulationSingleSlide;
